
import { useState, useEffect, useCallback, useMemo } from 'react';
import { detectUserCurrency } from '@/utils/locale-currency-map';
import { supabase } from '@/integrations/supabase/client';

interface ExchangeRatesCache {
  source: string;
  rates: Record<string, number>;
  cached_at: number;
  version: number;
  userCurrency: string;
}

const CACHE_KEY = 'exchange_rates';
const CURRENCY_CACHE_KEY = 'ip_detected_currency';
const CACHE_VERSION = 3;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function loadCachedRates(): ExchangeRatesCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed.version !== CACHE_VERSION ||
      Date.now() - parsed.cached_at > CACHE_TTL_MS
    ) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed as ExchangeRatesCache;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function loadCachedCurrency(): string | null {
  try {
    const raw = localStorage.getItem(CURRENCY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cached_at > CACHE_TTL_MS) {
      localStorage.removeItem(CURRENCY_CACHE_KEY);
      return null;
    }
    return parsed.currency || null;
  } catch {
    localStorage.removeItem(CURRENCY_CACHE_KEY);
    return null;
  }
}

export function useLocalCurrency() {
  const cachedCurrency = loadCachedCurrency();
  const [userCurrency, setUserCurrency] = useState<string>(cachedCurrency || detectUserCurrency());
  const [isCurrencyDetected, setIsCurrencyDetected] = useState(!!cachedCurrency);
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      // Step 1: Detect currency via IP if not cached
      let detectedCurrency = loadCachedCurrency();

      if (!detectedCurrency) {
        try {
          const { data, error } = await supabase.functions.invoke('get-approx-location');
          if (!error && data?.success && data?.data?.currency) {
            detectedCurrency = data.data.currency;
            localStorage.setItem(CURRENCY_CACHE_KEY, JSON.stringify({
              currency: detectedCurrency,
              cached_at: Date.now(),
            }));
          }
        } catch (err) {
          console.warn('IP currency detection failed, using browser locale:', err);
        }
      }

      if (!detectedCurrency) {
        detectedCurrency = detectUserCurrency();
      }

      if (!cancelled) {
        setUserCurrency(detectedCurrency);
        setIsCurrencyDetected(true);
      }

      // Step 2: Load exchange rates
      const cachedRates = loadCachedRates();
      if (cachedRates) {
        if (!cancelled) {
          setRates(cachedRates.rates);
          setIsLoading(false);
        }
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('get-exchange-rates');
        if (error) throw error;
        if (!cancelled && data?.rates) {
          setRates(data.rates);
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            ...data,
            version: CACHE_VERSION,
            userCurrency: detectedCurrency,
            cached_at: Date.now(),
          }));
        }
      } catch (err) {
        console.warn('Failed to fetch exchange rates, using original currency:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  /** Convert an amount from `fromCurrency` to user's local currency */
  const convert = useCallback(
    (amount: number, fromCurrency: string): number => {
      if (!isCurrencyDetected) return amount;
      const from = fromCurrency.toUpperCase();
      const to = userCurrency.toUpperCase();
      if (from === to) return amount;
      if (!rates) return amount;

      const fromRate = from === 'USD' ? 1 : rates[from.toLowerCase()];
      const toRate = to === 'USD' ? 1 : rates[to.toLowerCase()];

      if (!fromRate || !toRate) return amount;

      return Math.round(((amount / fromRate) * toRate) * 100) / 100;
    },
    [rates, userCurrency, isCurrencyDetected]
  );

  /** Format amount in user's local currency, e.g. '£79.00' */
  const formatLocal = useCallback(
    (amount: number, fromCurrency: string): string => {
      const from = fromCurrency.toUpperCase();
      if (!isCurrencyDetected) return formatCurrency(amount, from);
      const to = userCurrency.toUpperCase();

      if (from === to || !rates) {
        return formatCurrency(amount, from);
      }

      const converted = convert(amount, fromCurrency);
      return formatCurrency(converted, to);
    },
    [convert, rates, userCurrency, isCurrencyDetected]
  );

  /** Format with dual display, e.g. '£79.00 (≈ $100.00 USD)' */
  const formatDual = useCallback(
    (amount: number, fromCurrency: string): string => {
      const from = fromCurrency.toUpperCase();
      if (!isCurrencyDetected) return formatCurrency(amount, from);
      const to = userCurrency.toUpperCase();

      if (from === to || !rates) {
        return formatCurrency(amount, from);
      }

      const converted = convert(amount, fromCurrency);
      return `${formatCurrency(converted, to)} (≈ ${formatCurrency(amount, from)})`;
    },
    [convert, rates, userCurrency, isCurrencyDetected]
  );

  /** The exchange rate from USD to userCurrency */
  const rate = useMemo(() => {
    if (!isCurrencyDetected) return 1;
    if (userCurrency === 'USD') return 1;
    if (!rates) return 1;
    return rates[userCurrency.toLowerCase()] || 1;
  }, [rates, userCurrency, isCurrencyDetected]);

  return {
    userCurrency,
    isCurrencyDetected,
    rate,
    isLoading,
    convert,
    formatLocal,
    formatDual,
  };
}

// Always append the ISO currency code so users can tell CNY vs JPY (both ¥),
// USD vs CAD/AUD (all $), EUR from other symbols, etc. at a glance.
function formatCurrency(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  try {
    const formatted = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    // Avoid double-code (e.g. some locales already render "CN¥" or "US$")
    if (formatted.includes(code)) return formatted;
    return `${formatted} ${code}`;
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}
